import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useFetchErrorToast } from '@/hooks/use-fetch-error-toast';
import { timesheetsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface InScopeProductsResult {
  /** `true` once we have a settled response (success **or** error) for the
   *  current `(customerIds, window)` tuple — or there were no customers to
   *  fetch in the first place. Callers should pass `undefined` (not an
   *  empty `Set`) to the picker while `isLoaded` is false so the dropdown
   *  renders a flat list rather than mis-classifying every product as
   *  out-of-scope. */
  isLoaded: boolean;
  /** Per-customer in-scope product set. Every input customer has a Set in
   *  the map (possibly empty) once the request returns successfully; on
   *  error the map is empty and a toast surfaces the failure. */
  byCustomer: ReadonlyMap<string, ReadonlySet<string>>;
}

/**
 * Single batched query for the in-scope products of every team customer the
 * timesheet owner (`userId`) is on over the displayed window. The server
 * derives the customer set from that user's memberships, so this sends only
 * a user + window — no customer-id list (which previously grew the URL past
 * browser/proxy limits for users on many teams). One HTTP round trip, plus a
 * stable `isLoaded` flag (settled on success **or** error) so the consumer
 * can distinguish "still fetching" from "request done, may have failed."
 * Failures surface as a toast — silently leaving the picker unsectioned
 * forever was the prior shape of this bug.
 */
export function useInScopeProductsByCustomer(
  userId: string | undefined,
  fromDate: string,
  toDate: string,
): InScopeProductsResult {
  // Guard on the date window too: an optimistic timer placeholder carries an
  // empty `workDate` sentinel until the server-issued row swaps in, and the
  // server rejects an empty `fromDate`/`toDate` with a 400. Skipping the
  // request until the dates are real avoids a spurious "could not load
  // in-scope products" failure during that window.
  const enabled = Boolean(userId) && Boolean(fromDate) && Boolean(toDate);
  const query = useQuery({
    queryKey: queryKeys.timesheets.inScopeActivities({ userId: userId ?? '', fromDate, toDate }),
    queryFn: () => timesheetsApi.inScopeActivities({ userId, fromDate, toDate }),
    enabled,
    staleTime: 60_000,
  });

  useFetchErrorToast(query.error, 'in-scope products');

  // Stable Map: the `data` reference is the only dep — TanStack mints a
  // new object only when the response changes, so unrelated renders skip
  // the rebuild.
  const byCustomer = useMemo(() => {
    const m = new Map<string, ReadonlySet<string>>();
    for (const entry of query.data?.data ?? []) {
      m.set(entry.customerId, new Set(entry.productIds));
    }
    return m;
  }, [query.data]);

  // `isLoaded` is "we've completed at least one request for the current
  // tuple," whether it succeeded or failed. An empty customers list is
  // trivially loaded — there's nothing to fetch. Using `isFetched` (not
  // `isSuccess`) means a failed request doesn't trap the picker in a
  // permanent unsectioned state.
  const isLoaded = !enabled || query.isFetched;

  // Stable wrapper reference for callers that depend on referential
  // equality (memoed selectors, prop comparisons).
  return useMemo(() => ({ byCustomer, isLoaded }), [byCustomer, isLoaded]);
}
